import React, { useState, useRef, useEffect } from "react";
import "./Recorder.css";
import { predictWithWavBlob } from "./api";
import useTranslation from '../hooks/useTranslation';
import { analyzeAudioLevel, isLikelyNoiseResult, encodeWAV } from '../utils/audioAnalysis';
import { LuMic, LuInfo, LuAudioLines } from 'react-icons/lu';

const IDLE_BARS = [18, 34, 54, 78, 100, 62, 88, 42, 24];

function Recorder({ onResult, onRecordingStart, onRequireLogin, onAbort, variant = 'default', startSignal = 0 }) {
  const [isRecording, setIsRecording] = useState(false);
  const [, setAudioUrl] = useState(null);
  const [timer, setTimer] = useState(8); // Start at 8 seconds for countdown
  const [, setError] = useState("");
  const [, setInfo] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalText, setErrorModalText] = useState("");
  const [inlineError, setInlineError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const audioChunks = useRef([]);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationIdRef = useRef(null);
  const waveformCanvas = useRef(null);
  const intervalRef = useRef(null);
  // Auto-stop timer id for the 8-second cap
  const autoStopTimeoutRef = useRef(null);
  const { t } = useTranslation();

  function openErrorModal(message) {
    setError(message);
    setErrorModalText(message);
    setInlineError(message);
    setShowErrorModal(true);
    if (typeof onAbort === 'function') onAbort();
  }

  const startRecording = async () => {
    // Check login
    if (!localStorage.getItem("neuro_user_id")) {
      if (typeof onRequireLogin === "function") onRequireLogin();
      return;
    }
    setError("");
    setInlineError("");
    setInfo("[Recorder] Requesting mic access...");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000
        }
      });

      audioContextRef.current = new ((typeof window !== 'undefined' && window.AudioContext) || (typeof window !== 'undefined' && window.webkitAudioContext))();
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);

      analyserRef.current.fftSize = 128;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      // For waveform
      function drawWave() {
        const canvas = waveformCanvas.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const width = canvas.width;
        const height = canvas.height;
        ctx.clearRect(0, 0, width, height);

        analyserRef.current.getByteTimeDomainData(dataArrayRef.current);

        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#2bffbb";
        const sliceWidth = width / bufferLength;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = dataArrayRef.current[i] / 128.0;
          const y = (v * height) / 2;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          x += sliceWidth;
        }
        ctx.stroke();

        animationIdRef.current = requestAnimationFrame(drawWave);
      }
      drawWave();

      // PCM capture
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      audioChunks.current = [];

      processor.onaudioprocess = (e) => {
        const channelData = e.inputBuffer.getChannelData(0);
        audioChunks.current.push(new Float32Array(channelData));
      };

      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(processor);
      processor.connect(audioContextRef.current.destination);

      setIsRecording(true);
      setTimer(8); // Start countdown at 8 seconds
      // Decrement the UI timer every second from 8 to 0

      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => setTimer((t) => Math.max(t - 1, 0)), 1000);

      setInfo("[Recorder] Started recording.");



      // Enforce a hard 8-second recording limit

      // If the user does not stop manually, we automatically call the same

      // stop path after 8 seconds to keep the existing flow unchanged.

      if (autoStopTimeoutRef.current) clearTimeout(autoStopTimeoutRef.current);

      autoStopTimeoutRef.current = setTimeout(() => {

        // Trigger the exact same stop behavior as manual stop when countdown reaches 0

        stopRecording();

      }, 8000);
    } catch (e) {
      setError(t('Microphone access denied or not available.'));
      setInlineError(t('Microphone access denied or not available.'));
      setInfo("");
    }
  };

  const stopRecording = () => {
    if (typeof onRecordingStart === "function") onRecordingStart();

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }


    // Clear the 1s UI timer and the auto-stop guard

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (autoStopTimeoutRef.current) {

      clearTimeout(autoStopTimeoutRef.current);

      autoStopTimeoutRef.current = null;

    }
    setIsRecording(false);
    setInfo("[Recorder] Stopped recording. Processing WAV...");

    handleStop();
  };

  const handleStop = async () => {
    setIsProcessing(true);
    try {
      const pcmArrays = audioChunks.current;
      const totalLength = pcmArrays.reduce((sum, arr) => sum + arr.length, 0);
      const merged = new Float32Array(totalLength);
      let offset = 0;
      for (let arr of pcmArrays) {
        merged.set(arr, offset);
        offset += arr.length;
      }

      const wavBlob = encodeWAV(merged, 44100);
      const url = URL.createObjectURL(wavBlob);
      setAudioUrl(url);

      setInfo("[Recorder] Analyzing audio level...");
      const analysis = await Promise.race([
        analyzeAudioLevel(wavBlob),
        new Promise(resolve => setTimeout(() => resolve({ isSilent: false, reason: 'timeout' }), 3500))
      ]);

      if (analysis && analysis.isSilent) {
        setInfo("");
        openErrorModal(t('No speech detected. Please record again in a quieter place and speak clearly.'));
        return;
      }

      setInfo("[Recorder] Sending to model...");
      const predictRes = await predictWithWavBlob(wavBlob);
      if (predictRes && predictRes.data && isLikelyNoiseResult(predictRes.data)) {
        setInfo("");
        openErrorModal(t('Input seems to be background noise. Please record again in a quieter place and speak clearly.'));
        return;
      }
      setInfo("");
      setInlineError("");
      if (onResult) onResult(predictRes.data);
    } catch (err) {
      setInfo("");
      const isInvalidAudio = err.response?.data?.is_invalid_audio;
      const errMsg = isInvalidAudio
        ? t('Human speech not detected. Please speak clearly into the microphone.')
        : "[Recorder] Audio processing failed: " + (err.message || "Unknown error");
      openErrorModal(errMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Hero "Predict Emotion" action asks this recorder to begin.
  const lastSignal = useRef(0);
  useEffect(() => {
    if (!startSignal || startSignal === lastSignal.current) return;
    lastSignal.current = startSignal;
    if (!isRecording && !isProcessing) startRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSignal]);

  const formatTime = (secs) =>
    `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;

  if (variant === 'xi') {
    const status = inlineError
      ? 'error'
      : isProcessing
        ? 'processing'
        : isRecording
          ? 'recording'
          : 'idle';

    const statusLabel = {
      idle: t('Ready to listen'),
      recording: `${t('Listening')} · ${formatTime(timer)}`,
      processing: t('Reading your voice'),
      error: t('Tap to try again'),
    }[status];

    const statusEmoji = {
      idle: '',
      recording: '🎙️',
      processing: '✨',
      error: '⚠️',
    }[status];

    const bars = (live) => (
      <span className={`xi-wave-bars${live ? ' is-live' : ''}`} aria-hidden="true">
        {IDLE_BARS.map((h, i) => (
          <i key={i} style={live ? undefined : { height: `${h}%` }} />
        ))}
      </span>
    );

    return (
      <div className="xi-recorder">
        <div className="xi-recorder__lead">
          <span className="xi-recorder__icon" aria-hidden="true">
            <LuAudioLines />
          </span>
          <div className="xi-recorder__lead-text">
            <h2 className="xi-recorder__title">{t('Predict your emotion')}</h2>
            <p className="xi-recorder__sub">{t('in just 8 seconds')}</p>
          </div>
        </div>

        <div className="xi-recorder__core">
          {bars(isRecording)}
          <button
            type="button"
            className={`xi-mic${isRecording ? ' is-recording' : ''}${isProcessing ? ' is-processing' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isProcessing}
            aria-label={isRecording ? t('Stop recording') : t('Start recording')}
          >
            <LuMic />
            {isRecording && <span className="xi-countdown" aria-hidden="true" />}
          </button>
          {bars(isRecording)}
        </div>

        <p className="xi-recorder__hint">
          {isRecording ? t('Recording — tap to stop') : t('Tap and speak for 8 seconds')}
        </p>

        <div className="xi-recorder__status">
          <span
            className={`xi-status${status === 'error' ? ' is-error' : ''}${status === 'recording' ? ' is-live' : ''}`}
            role="status"
            aria-live="polite"
          >
            {statusEmoji && <span className="xi-status__emoji" aria-hidden="true">{statusEmoji}</span>}
            {statusLabel}
            <LuInfo className="xi-status__info" aria-hidden="true" />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="recorder-container">
      {showErrorModal && (
        <div className="login-modal-bg" onClick={() => setShowErrorModal(false)}>
          <div className="login-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, width: '90vw' }}>
            <button
              type="button"
              className="login-close-btn"
              aria-label={t('Close error')}
              onClick={() => setShowErrorModal(false)}
            >
              <span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 300, fontSize: '1.5em', lineHeight: 1 }}>×</span>
            </button>
            <h1 style={{ marginBottom: '0.7rem', fontSize: '1.6rem', fontWeight: 700, color: '#e53935', textAlign: 'center' }}>{t('No speech detected')}</h1>
            <div style={{ color: '#232323', fontSize: '1.05rem', textAlign: 'center', padding: '0.4rem 0 0.6rem' }}>{errorModalText}</div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
              <button className="login-btn submit" onClick={() => setShowErrorModal(false)} style={{ minWidth: 120 }}>{t('OK')}</button>
            </div>
          </div>
        </div>
      )}
      {/* Motivational quote */}
      <div className="recorder-quote">
        Predict your emotion in just 8 seconds
      </div>

      <div className="recorder-ui">
        <button
          className={`record-mic-btn-emoji${isRecording ? " recording" : ""}`}
          onClick={isRecording ? stopRecording : startRecording}
          aria-label={isRecording ? t('Stop recording') : t('Start recording')}
        >
          <span role="img" aria-label="mic">🎤</span>
        </button>
        <span
          className={`recorder-timer ${timer <= 3 ? 'recorder-timer-warning' : ''}`}
        >
          {formatTime(timer)}
        </span>
      </div>

      <canvas
        ref={waveformCanvas}
        width={340}
        height={48}
        className="waveform-canvas"
        style={{ display: isRecording ? "block" : "none" }}
      />


    </div>
  );
}

export default Recorder;
